import React, { useEffect, useState } from "react";
import { Button, DatePicker, Form, Input, Modal, Select, Radio } from "antd";
import resources from "../../../../resources";
import { formatEnum } from "../../../../utility/utility";
import { Leave_Duration } from "../../../../constant/constant";
import { ClockCircleOutlined, DeleteOutlined, ScheduleOutlined } from "@ant-design/icons";
import { useLeaveTypes } from "../../../../custom_hooks/useLeaveTypes";
import dayjs from "dayjs";
import server from "../../../../server";
import EmployeeSelect from "../../../../page-components/common/employee-select/employee-select";
import Locale from "../../../../core/locale/locale";
import { IAddLeaveModal } from "../leave-request/leave-request.types";
import toastify from "../../../../common/toastify";
import { X } from "lucide-react";

const initialLeaveState = {
    id: 0,
    startDate: '',
    endDate: '',
    session: 'FULL_DAY',
    days: 0,
    dateDisable: true
}

const ApplyLeaveModal: React.FC<IAddLeaveModal> = (
    {
        isOpen,
        title,
        isAdmin,
        setIsOpen,
        pageNumber,
        selectedEmployee,
        fetchLeaveRequests,
        field,
        direction
    }
) => {
    const [form] = Form.useForm();
    const [formState, setFormState] = useState({
        leaves: [initialLeaveState],
        totalDays: 0,
        isSubmitDisabled: true,
        role: ''
    });
    const tempResources = resources.leaveRequest;
    const {leaveTypes, loading} = useLeaveTypes();

    useEffect(() => {
        if (isOpen) {
            form.setFieldsValue({
                leaves: formState.leaves,
            });
            setFormState((prev) => ({
                ...prev,
                role: isAdmin ? 'admin' : 'employee'
            }))
        }
    }, [form, isOpen]);

    function formatToPayload(formValues: any) {
        const individualLeaves = formValues.leaves.flatMap((item: any) => {
            const result = [];
            let current = item.startDate;
            const end = item.endDate;
            while (current.isSame(end) || current.isBefore(end)) {
                const day = current.day();
                if (day !== 0 && day !== 6) { // Skip weekends
                    result.push({
                        date: current.format("YYYY-MM-DD"),
                        session: item.session
                    });
                }
                current = current.add(1, "day");
            }
            return result;
        });

        return {
            ...formValues,
            leaves: individualLeaves,
            employeeId: formValues?.employeeId ? formValues?.employeeId : selectedEmployee?.id,
        }
    }

    const handleFormSubmit = async () => {
        const formValues = await form.getFieldsValue();
        const formattedPayload = formatToPayload(formValues)
        try {
            const employee = await server.employee.employeeDetailsByID(formattedPayload.employeeId,
                {"include-leave-entitlements": true});
            const response = employee.leaveEntitlements;
            formattedPayload.entitlementId = response.find((item: any) => item.leaveType === formattedPayload.leaveType)?.id;

            if (!formattedPayload.entitlementId) {
                toastify({
                    message: 'Currently No Leaves are Entitled to you',
                    title: 'Error',
                    isSuccessful: false,
                });
                setIsOpen(false);
                return;
            }

            //create leave-request
            await server.leave.request.create(formattedPayload);

            //call fetch function: to get all latest leaves
            await fetchLeaveRequests(pageNumber,field,direction);
            form.resetFields();
            setFormState((prev) => ({
                ...prev,
                leaves: [initialLeaveState],
                totalDays: 0
            }))

            setIsOpen(false);

            toastify({
                message: tempResources.requestCreatedSuccessMessage,
                title: 'Leave Created',
                isSuccessful: true,
            });
        } catch (e) {
            console.error(e);
            toastify({
                message: tempResources.requestCreatedErrorMessage,
                title: 'Error',
                isSuccessful: false,
            });
        }
    };

    const handleSessionValueChange = (e: any, index: number) => {
        const value = e.target.value;
        const isHalfDay = ['FIRST_HALF', 'SECOND_HALF', 'First_Half', 'Second_Half'].includes(value);
        setFormState(prev => {
            const updated = prev.leaves.map((item, i) =>
                i === index ? {...item} : item
            );
            const item = updated[index];
            item.session = value;
            item.dateDisable = isHalfDay;
            // if half day, make endDate = startDate
            if (isHalfDay && item.startDate) {
                item.endDate = item.startDate;
            }
            item.days = calculateDateDifference(item.startDate, item.endDate, item.session);
            // total day count
            const total = updated.reduce((sum, l) => sum + (l.days || 0), 0);

            form.setFieldsValue({
                leaves: updated
            });

            return {...prev, leaves: updated, totalDays: total};
        });
    }

    const handleCancel = () => {
        form.resetFields();
        setFormState((prev) => ({
            ...prev,
            leaves: [initialLeaveState],
            totalDays: 0
        }))
        setIsOpen(false);
    };

    const handleAddLeave = () => {
        setFormState((prev) => ({
            ...prev,
            leaves: [...prev.leaves, initialLeaveState]
        }))
    };

    const handleRemoveLeaveItem = (indexToRemove: number) => {
        const leavesRemaining = formState.leaves.filter((_, index) => index !== indexToRemove);
        setFormState((prev) => ({
            ...prev,
            leaves: leavesRemaining,
            totalDays: leavesRemaining.reduce((sum, l) => sum + (l.days || 0), 0)
        }));
    };

    function calculateDateDifference(start: any, end: any, session: string) {
        if (!start || !end) return 0;

        let startDate = dayjs(start);
        let endDate = dayjs(end);

        let workingDays = 0;

        // Loop through all days and count only weekdays
        let current = startDate.clone();

        while (current.isBefore(endDate, "day") || current.isSame(endDate, "day")) {
            const day = current.day(); // 0 = Sunday, 6 = Saturday

            if (day !== 0 && day !== 6) {
                workingDays++;
            }
            current = current.add(1, "day");
        }

        // Apply half-day logic only when exactly 1 working day
        if (workingDays === 1) {
            if (session === "FIRST_HALF" || session === "SECOND_HALF") {
                return 0.5;
            }
        }
        return workingDays;
    }

    function validateBeforeEnableSubmit() {
        const errors = form.getFieldsError();
        const hasErrors = errors.some(field => field.errors.length > 0);

        if (hasErrors) return false;

        const values = form.getFieldsValue();

        // leaveType mandatory
        if (!values.leaveType) return false;

        // admin must select employee
        if (isAdmin && !values.employeeId) return false;

        // reason mandatory
        if (!values.reason?.trim()) return false;

        // validate all leave entries
        if (!values.leaves || values.leaves.length === 0) return false;

        for (const item of values.leaves) {
            if (!item.session) return false;
            if (!item.startDate) return false;
            if (!item.endDate) return false;
        }
        return true;
    }

    const disabledDate = (
        currentDate: any,
        role: string,
        startDate?: any,
        isEndDate?: boolean
    ) => {
        const selectedDate = dayjs(currentDate);

        if (selectedDate.day() === 0 || selectedDate.day() === 6) {
            return true;
        }

        if (isEndDate && startDate) {
            if (selectedDate.isBefore(dayjs(startDate), "day")) {
                return true;
            }
        }

        const today = dayjs();
        switch (role) {
            case "admin":
                //before 3 months back
                if (selectedDate.isBefore(today.subtract(3, "month"), "day")) {
                    return true;
                }

                // admin should NOT select future dates including today
                if (selectedDate.isAfter(today, "day") || selectedDate.isSame(today, "day")) {
                    return true;
                }

                return false;

            case "employee":
                if (
                    selectedDate.isBefore(today.subtract(1, "month"), "day") ||
                    selectedDate.isAfter(today.add(6, "month"), "day")
                ) {
                    return true;
                }
                return false;

            default:
                return false;
        }
    };

    function handleDateChange(date: any, index: number, field: "startDate" | "endDate") {
        setFormState(prev => {
            const updatedLeaves = [...prev.leaves];
            // set the startDate/endDate when date is picked form datePicker
            const item = {...updatedLeaves[index], [field]: date};

            if (item.session && ['FIRST_HALF', 'SECOND_HALF', 'First_Half', 'Second_Half'].includes(item.session)) {
                item.dateDisable = true;
                item.endDate = date; // endDate always equals startDate
            } else {
                item.dateDisable = false;
            }

            // Determine actual end date for days calculation
            const end = item.dateDisable ? item.startDate : item.endDate;

            updatedLeaves[index] = item;

            item.days = calculateDateDifference(item.startDate, end, item.session);

            form.setFieldsValue({
                leaves: updatedLeaves,
            });
            const total = updatedLeaves.reduce((sum, item) => sum + (item.days || 0), 0);
            //update the totalDays in formState
            return {
                ...prev,
                leaves: updatedLeaves,
                totalDays: total
            };
        });
    }

    const modalFooter = () => (
        <div className={'leave-request-modal__footer'}>
            <Button type={'primary'} onClick={handleFormSubmit} disabled={formState.isSubmitDisabled}>Submit
                Leave</Button>
            <Button type={'default'} onClick={handleCancel}>{tempResources.cancelButton}
            </Button>
        </div>
    );

    const renderTotalDays = () => {
        if (formState.totalDays) {
            return (
                <div className={'leave-request-form leave-day-count total'}>
                    <label className={'leave-day-label'}>Total Leave Applied : </label>
                    <span className={'total-count'}>{formState.totalDays} Days</span>
                </div>
            )
        }
    }

    return (
        <Modal
            title={title && title.length > 0 ? title : 'Apply Leave'}
            open={isOpen}
            className={"hrms-app leave-request-modal"}
            onCancel={handleCancel}
            footer={modalFooter}
        >
            <Form
                form={form}
                layout="horizontal"
                className={"leave-request-form"}
                onFieldsChange={() => {
                    setFormState(prev => ({
                        ...prev,
                        isSubmitDisabled: !validateBeforeEnableSubmit()
                    }));
                }}
            >
                <div className={'leave-request-form__employee-leave-wrapper'}>
                    {isAdmin &&
                    <Form.Item label={"Select Employee"}
                               name={"employeeId"}
                               rules={[{required: true, message: 'Please select employee'}]}>
                        <EmployeeSelect
                            includeAllSelectOption={false}
                            placeholder="Select employee name"
                        />
                    </Form.Item>
                    }
                    <Form.Item
                        label={tempResources.form.leaveTypeLabel}
                        name="leaveType"
                        className={"leave-request-form__item"}
                        rules={[{required: true, message: 'Please input Leave Type!'}]}>
                        <Select placeholder={loading ? "Loading..." : "Select leave type"} disabled={loading}>
                            {!loading && leaveTypes.map((leave) => (
                                <Select.Option key={leave.id} value={leave.type}>
                                    {formatEnum(leave.type)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                <div className={'leave-request-form__leave-details-container'}>
                    <div className={'schedule-heading'}>
                        <h4><ScheduleOutlined/>Leave Schedule</h4>
                        <Button
                            type="default"
                            onClick={handleAddLeave}
                            className={'add-btn'}
                        >{tempResources.form.addDateButtonText}</Button>
                    </div>
                    <div className={'leave-request-form__leave-detail-list'}>
                        {formState.leaves.map((leave, index) => (
                            <div className={'leave-request-form__leave-item-wrapper'} key={index}>

                                <span className={'close-btn'} onClick={() => handleRemoveLeaveItem(index)}>
                                    <X/>
                                </span>


                                <Form.Item
                                    label={
                                        <span><ClockCircleOutlined/>{tempResources.form.leaveSessionLabel}</span>}
                                    name={['leaves', index, 'session']}
                                    rules={[{required: true, message: 'Please select a session'}]}
                                >
                                    <Radio.Group buttonStyle="solid" className={'session-opts'}
                                                 defaultValue={'FULL_DAY'}
                                                 onChange={(e) => handleSessionValueChange(e, index)}>
                                        {(Object.keys(Leave_Duration) as Array<keyof typeof Leave_Duration>).map((key) =>
                                            <Radio.Button value={key} key={key}>
                                                {Leave_Duration[key]}
                                            </Radio.Button>
                                        )}
                                    </Radio.Group>
                                </Form.Item>
                                <div className={'leave-request-form__date-wrapper'}>
                                    <Form.Item
                                        key={index}
                                        label={tempResources.form.leaveStartDateLabel}
                                        name={['leaves', index, 'startDate']}
                                        className={"leave-request-form__inner-item"}
                                        rules={[{required: true, message: 'Please input leave days!'}]}>
                                        <DatePicker
                                            format={Locale.displayDate}
                                            disabledDate={(date) =>
                                                disabledDate(date, formState.role)
                                            }
                                            onChange={(date) => handleDateChange(date, index, 'startDate')}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        key={`${index}end`}
                                        label={tempResources.form.leaveEndDateLabel}
                                        name={['leaves', index, 'endDate']}
                                        className={"leave-request-form__inner-item"}
                                        rules={[{required: true, message: 'Please input leave days!'}]}
                                    >
                                        <DatePicker
                                            format={Locale.displayDate}
                                            disabledDate={(date) =>
                                                disabledDate(
                                                    date,
                                                    formState.role,
                                                    leave.startDate,
                                                    true
                                                )
                                            }
                                            disabled={leave.dateDisable}
                                            onChange={(date) => handleDateChange(date, index, 'endDate')}
                                        />
                                    </Form.Item>
                                </div>
                                {leave.days > 0 ? <div className={'leave-request-form leave-day-count'}><label
                                    className={'leave-day-label'}>Days: </label><span
                                    className={'count'}>{leave.days}</span></div> : null}
                            </div>
                        ))}
                    </div>
                </div>
                {renderTotalDays()}
                <Form.Item
                    label={tempResources.form.reasonLabel}
                    name="reason"
                    rules={[{required: true, message: 'Please input Reason!'}]}
                    className={"leave-request-form__item"}
                >
                    <Input.TextArea
                        rows={4}
                        placeholder="Enter the reason..."
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default ApplyLeaveModal;
